import { Modal } from "components/designV2/modal/modal";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { Text } from "components/designV2/text/text";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import { SalaryNotice } from "model/Salary";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { isAdjustableMonth } from "web-apps/company/utils/timeAndDate";
import {
  SalaryPageBottomMargin,
  PeriodNavigatorContainer,
  TooltipContainer,
} from "../salary.styled";
import { Pagination } from "../../general/pagination.component";
import { FileViewer } from "../../fileViewer/fileViewer.component";
import { ModalState } from "../individualSalary.component";
import { AdjustSalaryModal } from "../adjustSalaryModal.component";
import { useMonthlySalary } from "./monthlySalary.hook";
import { MonthlySalaryRow } from "./monthlySalaryRow.component";
import { EmptyStateText, SalaryList } from "./monthlySalary.styled";

interface AdjustModalState {
  open: boolean;
  salaryNoticeId?: number;
  workerId?: number;
  workerName?: string;
}

export const MonthlySalary = (): JSX.Element => {
  const { t } = useTranslation();
  const [{ company, companyWorkerImages, workerIdToExternalId }] =
    useCompanyStore();
  const {
    salaryNotices,
    currentDate,
    setCurrentDate,
    page,
    setPage,
    totalPages,
    getSalaries,
  } = useMonthlySalary(company?.id);
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    workerId: undefined,
    pdfId: undefined,
    period: undefined,
    workerName: undefined,
  });
  const [adjustModalState, setAdjustModalState] = useState<AdjustModalState>({
    open: false,
  });

  const openSalaryPdf = useCallback((salary: SalaryNotice) => {
    setModalState({
      open: true,
      workerId: salary.workerId,
      pdfId: salary.id,
      period: salary.period,
      workerName: salary.workerName,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      open: false,
      workerId: undefined,
      pdfId: undefined,
      period: undefined,
      workerName: undefined,
    });
  }, []);

  const openAdjustModal = useCallback((salary: SalaryNotice) => {
    setAdjustModalState({
      open: true,
      salaryNoticeId: salary.id,
      workerId: salary.workerId,
      workerName: salary.workerName,
    });
  }, []);

  const closeAdjustModal = useCallback(() => {
    setAdjustModalState({ open: false });
  }, []);

  const handleAdjustSaved = useCallback(() => {
    closeAdjustModal();
    getSalaries();
  }, [closeAdjustModal, getSalaries]);

  const { open, workerId, pdfId, period, workerName } = modalState;

  const canAdjustForDisplayedMonth = isAdjustableMonth(currentDate);

  return (
    <SalaryPageBottomMargin>
      <TooltipContainer>
        <TooltipHover text={t("Salary.MonthlySalarySlipInfo")} arrow="up">
          <Text typography="s1">
            {t(`Salary.WhatIs`, {
              name: t("Salary.MonthlySalary").toLowerCase(),
            })}
          </Text>
        </TooltipHover>
      </TooltipContainer>

      <PeriodNavigatorContainer>
        <PeriodNavigator
          currentDate={currentDate}
          mode="month"
          onChange={setCurrentDate}
        />
      </PeriodNavigatorContainer>

      {salaryNotices.length === 0 ? (
        <EmptyStateText>
          <Text typography="b2" color={ColorV2.Blue1} align="center">
            {t("Salary.PeriodNotClosed", {
              month: `${t(
                `CompanyTimeReport.${currentDate.month()}`,
              ).toLowerCase()}`,
            })}
          </Text>
        </EmptyStateText>
      ) : (
        <>
          <SalaryList>
            {salaryNotices.map((salary) => {
              const currency = salary.currency === "SEK" ? " kr" : "";
              const externalId = workerIdToExternalId[salary.workerId];

              return (
                <MonthlySalaryRow
                  key={`monthlysalary-${salary.id}`}
                  salary={salary}
                  avatarUrl={companyWorkerImages[externalId]}
                  currency={currency}
                  canAdjustForDisplayedMonth={canAdjustForDisplayedMonth}
                  onOpenPdf={openSalaryPdf}
                  onOpenAdjust={openAdjustModal}
                />
              );
            })}
          </SalaryList>

          {totalPages > 1 && (
            <Pagination setPage={setPage} page={page} totalPages={totalPages} />
          )}
        </>
      )}

      {open && company && workerId && pdfId && period && workerName && (
        <Modal
          isOpen={open}
          onClose={closeModal}
          label={t("Salary.ViewSalaryNoticeTooltip")}
          size="large"
        >
          <FileViewer
            fetchEndpoint={() =>
              Api().company.salary.generateSalaryNotice(
                company.id,
                workerId,
                pdfId,
              )
            }
            filename={`SalaryNotice_${period}_${workerName}.pdf`}
            type="application/pdf"
          />
        </Modal>
      )}

      {company &&
        adjustModalState.open &&
        adjustModalState.salaryNoticeId &&
        adjustModalState.workerId &&
        adjustModalState.workerName && (
          <AdjustSalaryModal
            open={adjustModalState.open}
            companyId={company.id}
            salaryNoticeId={adjustModalState.salaryNoticeId}
            workerId={adjustModalState.workerId}
            workerName={adjustModalState.workerName}
            onClose={closeAdjustModal}
            onSaved={handleAdjustSaved}
          />
        )}
    </SalaryPageBottomMargin>
  );
};
